import React, { Fragment, useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import BugIcon from '@atlaskit/icon/core/bug';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
import Tabs from '@atlaskit/tabs';
import { token } from '@atlaskit/tokens';

import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { QueryIntent } from '../../constants';

import { messages } from './messages';
import { DebuggerTabList } from './tab-list';
import { MinionsInvokedPanel } from './tab-panels/minions-invoked-panel';
import { PluginInvocationsPanel } from './tab-panels/plugin-invocations-panel';
import { RankedSearchResultsPanel } from './tab-panels/ranked-search-results-panel';
import { RawJsonPanel } from './tab-panels/raw-json-panel';
import { RawSearchResultsPanel } from './tab-panels/raw-search-results-panel';
import { RequestOverviewPanel } from './tab-panels/request-overview-panel';
import { normalizeDebuggerData } from './utils';

const ColoredBugIcon = () => <BugIcon color={token('color.icon.subtlest')} label="" />;

export const OnlineDebuggerButton = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { commonAttributes, smartAnswersRoute } = useAIAnswerContext();
	const [isSAINDebuggerModalOpen, setIsSAINDebuggerModalOpen] = useState(false);

	const isNaturalLanguageQuery = smartAnswersRoute === QueryIntent.NATURAL_LANGUAGE_QUERY;

	const openDebuggerModal = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		setIsSAINDebuggerModalOpen(true);
	}, []);
	const closeDebuggerModal = useCallback(() => setIsSAINDebuggerModalOpen(false), []);

	const { extraAttributes } = commonAttributes;
	const debuggerData = useMemo(() => normalizeDebuggerData(extraAttributes), [extraAttributes]);

	return (
		<Fragment>
			<IconButton
				icon={ColoredBugIcon}
				label={formatMessage(messages.ai_debugger_icon_label)}
				onClick={openDebuggerModal}
				appearance="subtle"
				isTooltipDisabled={false}
				spacing="compact"
				testId="sain-online-debugger-button"
				color={token('color.icon.subtlest')}
				shape={fg('kd_sain_feedback_collector_ux_uplift_rollout') ? 'circle' : 'default'}
			/>

			<ModalTransition>
				{isSAINDebuggerModalOpen && (
					<Modal onClose={closeDebuggerModal} width="x-large" testId="sain-online-debugger-modal">
						<ModalHeader hasCloseButton>
							<ModalTitle>{formatMessage(messages.ai_debugger_modal_title)}</ModalTitle>
						</ModalHeader>
						<ModalBody>
							<Tabs id="sain-online-debugger-tabs">
								<DebuggerTabList
									isNaturalLanguageQuery={isNaturalLanguageQuery}
									hasMinionOutputs={debuggerData.minions.hasMinionOutputs}
								/>
								<RequestOverviewPanel
									{...debuggerData.requestOverview}
									smartAnswersRoute={smartAnswersRoute}
									isNaturalLanguageQuery={isNaturalLanguageQuery}
								/>
								{isNaturalLanguageQuery && (
									<RawSearchResultsPanel searchResults={debuggerData.rawSearch.searchResults} />
								)}
								{isNaturalLanguageQuery && (
									<RankedSearchResultsPanel
										rankedDocumentsWithChunks={debuggerData.rankedSearch.rankedDocumentsWithChunks}
									/>
								)}
								{isNaturalLanguageQuery && !debuggerData.minions.hasMinionOutputs && (
									<PluginInvocationsPanel
										nonSearchPluginInvocations={
											debuggerData.pluginInvocations.nonSearchPluginInvocations
										}
									/>
								)}
								{isNaturalLanguageQuery && debuggerData.minions.hasMinionOutputs && (
									<MinionsInvokedPanel
										minionOutputs={debuggerData.minions.nonSearchMinionOutputs}
									/>
								)}
								{isNaturalLanguageQuery && <RawJsonPanel rawJson={debuggerData.rawJson.rawJson} />}
							</Tabs>
						</ModalBody>
						<ModalFooter />
					</Modal>
				)}
			</ModalTransition>
		</Fragment>
	);
};
